// English versions of the Help page guides (Help.jsx holds the Turkish ones
// and picks per UI language). Long-form prose doesn't fit the key-by-key
// messages pattern, so each guide is written out per language — keep both in
// step when a guide changes. The add-in's own ribbon/taskpane labels are
// Turkish, so they're quoted as-is with an English gloss.
import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui';
import AdminCenterHome from '../img/admin-center-home.png';
import AdminCenterIntegratedApps from '../img/admin-center-integrated-apps.png';
import AdminCenterUploadManifest from '../img/admin-center-upload-manifest.png';

const scrollToSection = (id) => {
  const el = document.querySelector(`[data-section="${id}"]`);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

export function AddinDeployGuideEn() {
  return (
    <Card className="help-card">
      <h2>How to Deploy the Outlook Add-in</h2>
      <p className="help-intro">
        The add-in is deployed centrally from the Microsoft 365 admin center — users don’t install it
        themselves. It’s configured once and reaches the selected users automatically.
      </p>

      <ol className="help-steps">
        <li>
          <strong>Open the Microsoft 365 admin center</strong>
          <p>
            Sign in at <code>admin.microsoft.com</code> with an account that has the Global Admin or
            Exchange Admin role.
          </p>
          <img className="help-step-screenshot" src={AdminCenterHome} alt="Microsoft 365 admin center home screen" />
        </li>
        <li>
          <strong>Open the Integrated apps page</strong>
          <p>
            In the left menu, go to <em>Settings → Integrated apps</em>.
          </p>
          <img
            className="help-step-screenshot"
            src={AdminCenterIntegratedApps}
            alt="The Integrated apps page in the admin center with the Get apps button"
          />
        </li>
        <li>
          <strong>Upload a new app</strong>
          <p>
            Choose <em>Upload custom apps</em> and enter the manifest URL:
          </p>
          <pre className="help-code">https://imza.martinci.com/outlook-addin/manifest-validated.xml</pre>
          <img
            className="help-step-screenshot"
            src={AdminCenterUploadManifest}
            alt="The Deploy New App panel with the manifest URL entered"
          />
        </li>
        <li>
          <strong>Choose who gets it</strong>
          <p>
            For a first rollout, or when re-enabling after an incident, we recommend choosing{' '}
            <strong>a small pilot group instead of the entire organization</strong> (e.g. just the IT
            team). After a few trouble-free days, widen the scope step by step.
          </p>
        </li>
        <li>
          <strong>Confirm the deployment</strong>
          <p>
            Once confirmed, Microsoft deploys the add-in to the selected users’ mailboxes. According to
            Microsoft’s documentation it can take <strong>24–72 hours</strong> to appear in Outlook, and
            users need to fully close and reopen Outlook — see the{' '}
            <button type="button" className="help-inline-link" onClick={() => scrollToSection('cache-refresh')}>
              update delay guide
            </button>.
          </p>
        </li>
      </ol>
    </Card>
  );
}

export function AddinUpdateGuideEn({ currentVersion }) {
  return (
    <Card className="help-card" data-section="addin-update">
      <h2>How to Update the Outlook Add-in</h2>
      <p className="help-intro">
        The add-in changes in two ways, and only one of them arrives by itself.
      </p>

      <ol className="help-steps">
        <li>
          <strong>Content changes — nothing for you to do</strong>
          <p>
            The add-in panel’s look, the signature logic and bug fixes load live from our server. When we
            publish, your users see the new version the next time it opens (usually right away).
          </p>
        </li>
        <li>
          <strong>Manifest changes — you need to update them in the admin center</strong>
          <p>
            The manifest is the add-in’s definition file: what the ribbon button does, which permissions it
            asks for, its name and icon. The Microsoft 365 admin center keeps a <strong>copy</strong> of this
            file as it was at upload time and doesn’t pick up changes on our server by itself. When we
            release a version that changes the manifest we’ll let you know, and you’ll need to follow the
            steps below.
          </p>
        </li>
      </ol>

      <div className="help-callout">
        <strong>Current published manifest version:</strong>{' '}
        {currentVersion === null ? 'loading…' : currentVersion ? <code>{currentVersion}</code> : 'couldn’t be read'}
        <br />
        If the add-in version in the admin center is lower than this, your add-in is out of date.
      </div>

      <h3>Steps</h3>
      <ol className="help-steps">
        <li>
          <strong>Find the add-in in the admin center</strong>
          <p>
            In <code>admin.microsoft.com</code> &gt; <em>Settings → Integrated apps</em>, select our add-in
            and check its current version number.
          </p>
        </li>
        <li>
          <strong>Click Update</strong>
          <p>
            Select the add-in, use the <em>Update</em> button and enter the manifest address (menu names
            may differ slightly depending on Microsoft’s portal version):
          </p>
          <pre className="help-code">https://imza.martinci.com/outlook-addin/manifest-validated.xml</pre>
          <p>
            There’s no need to remove and re-add the add-in; updating refreshes the existing one in place.
          </p>
        </li>
        <li>
          <strong>Have users restart Outlook</strong>
          <p>
            Outlook needs to be fully closed and reopened. According to Microsoft’s documentation, a new
            button or behavior can take <strong>24–72 hours</strong> to show up in the ribbon. If it still
            looks old after that, try the{' '}
            <button type="button" className="help-inline-link" onClick={() => scrollToSection('cache-refresh')}>
              cache clearing steps
            </button>.
          </p>
        </li>
      </ol>

      <p className="help-note">
        Note: the add-in’s identity (Id) and domain never change, so users aren’t asked for consent again
        during an update.
      </p>
    </Card>
  );
}

export function CacheRefreshGuideEn() {
  return (
    <Card className="help-card" data-section="cache-refresh">
      <h2>Why Updates Are Delayed and How to Speed Them Up</h2>
      <p className="help-intro">
        When we publish a fix on our server, it can take a while to show up in users’ Outlook for one of
        three reasons, depending on the type of change.
      </p>

      <ol className="help-steps">
        <li>
          <strong>Add-in content (panel, signature logic, bug fixes)</strong>
          <p>
            Loads live from our server, with no delay. If the old version still shows, the cause is usually
            the local cache in item 3.
          </p>
        </li>
        <li>
          <strong>Manifest changes (button behavior, permissions, name/icon)</strong>
          <p>
            These don’t arrive by themselves: the admin center keeps a copy of the add-in and uses the old
            copy until an administrator clicks <em>Update</em> — see the{' '}
            <button type="button" className="help-inline-link" onClick={() => scrollToSection('addin-update')}>
              add-in update guide
            </button>. Even after updating, according to Microsoft’s documentation it can take{' '}
            <strong>24–72 hours</strong> to show in the ribbon; that time passes entirely on Microsoft’s
            side and can’t be sped up from our server.
          </p>
        </li>
        <li>
          <strong>The local cache on the user’s own computer (Wef cache)</strong>
          <p>
            Every Outlook also stores the add-in files and the manifest on its own computer. Even when the
            admin center is up to date, a user’s Outlook may keep showing the old copy; clearing this cache
            makes it fetch the new copy right away.
          </p>
        </li>
      </ol>

      <div className="help-callout help-callout-steps">
        <strong>Clearing the cache in classic Outlook (Windows):</strong>
        <ol>
          <li>Close Outlook completely.</li>
          <li>
            Press Windows key + R and enter:{' '}
            <code>%LOCALAPPDATA%\Microsoft\Office\16.0\Wef</code>
          </li>
          <li>Delete all files and folders in the folder that opens.</li>
          <li>Reopen Outlook.</li>
        </ol>
      </div>

      <div className="help-callout help-callout-steps">
        <strong>Clearing the cache in Outlook (Mac):</strong>
        <ol>
          <li>Quit Outlook completely (<code>Command + Q</code>).</li>
          <li>
            In Finder, use <strong>Go</strong> &gt; <strong>Go to Folder…</strong> (<code>Shift + Command + G</code>)
            to open each of the folders below in turn and delete <strong>their contents</strong> (not the
            folder itself; skip any that don’t exist):
            <ul>
              <li><code>~/Library/Containers/com.microsoft.Outlook/Data/Documents/wef</code></li>
              <li><code>~/Library/Containers/com.microsoft.Outlook/Data/Library/Caches</code></li>
              <li><code>~/Library/Containers/com.microsoft.Outlook/Data/Library/Application Support/Microsoft/Office/16.0/Wef</code></li>
              <li><code>~/Library/Containers/com.Microsoft.OsfWebHost/Data</code></li>
            </ul>
          </li>
          <li>Reopen Outlook.</li>
        </ol>
      </div>

      <p className="help-body">
        These steps only clear the old copy on the computer. If the manifest in the admin center isn’t up
        to date (item 2), clearing alone won’t change anything — update the add-in first.
      </p>
    </Card>
  );
}

export function MySignatureGuideEn({ isAdmin }) {
  return (
    <Card className="help-card" data-section="my-signature">
      <h2>How to Choose My Signature</h2>
      <p className="help-intro">
        Your signature is added automatically in Outlook to new emails, replies and forwards. If your
        administrator has given you more than one template or language, you can choose which one is used.
        Your choice applies on all your devices — Outlook desktop, Outlook on the web, iPhone and Android.
      </p>

      <h3>From the portal (on any device, including your phone)</h3>
      <ol className="help-steps">
        <li>
          <strong>Sign in to the portal</strong>
          <p>
            Open <code>imza.martinci.com</code> in your browser, click <strong>Sign in with Microsoft</strong>{' '}
            and sign in with your work email account. No separate password is needed.
          </p>
        </li>
        <li>
          <strong>Open the <Link to="/imzam">My Signature</Link> page</strong>
          <p>The top of the page shows your signature as it currently looks.</p>
        </li>
        <li>
          <strong>Choose the template and language</strong>
          <p>
            Tap your choice in the <strong>Template</strong> and <strong>Language</strong> sections. It’s
            saved instantly and the preview updates.
          </p>
        </li>
      </ol>

      <h3>From Outlook desktop or web</h3>
      <ol className="help-steps">
        <li>
          <strong>Open a new email</strong>
          <p>
            Click the <strong>İmza Ekle</strong> (Add Signature) button in the <strong>Martinci</strong> group
            on the ribbon (in Outlook on the web, the add-in icon may be under the <strong>…</strong> menu);
            the add-in panel opens on the right.
          </p>
        </li>
        <li>
          <strong>Choose the template (İmza Şablonu) and language (Dil)</strong>
          <p>
            Your choice is saved. To also change the signature in the email that’s open, click{' '}
            <strong>İmzayı Ekle</strong> (Insert Signature) — the signature isn’t added twice, it’s replaced
            with the new one.
          </p>
        </li>
      </ol>

      <div className="help-callout help-callout-steps">
        <strong>Good to know</strong>
        <ul>
          <li>Your choice applies to emails you open from now on; drafts that are already open don’t change by themselves.</li>
          <li>
            Outlook’s iPhone and Android apps have no add-in panel (a Microsoft limitation). To choose from
            your phone, use the My Signature page in the portal.
          </li>
          <li>
            If you don’t see any template or language options, your administrator has set up a single
            signature for you. If you’d like a different template, ask your administrator.
          </li>
        </ul>
      </div>

      {isAdmin && (
        <div className="help-callout help-callout-steps">
          <strong>For administrators — giving users options</strong>
          <ul>
            <li>
              On the <Link to="/users">People</Link> page, click a person and check the templates they may
              choose from under <strong>Selectable Templates</strong>. The choice appears once at least two
              templates are checked.
            </li>
            <li>
              Language options come from the template’s own language variants: edit the template on{' '}
              <Link to="/signatures">Signatures</Link> and add languages.
            </li>
            <li>If a personal signature is set up for the user, no options are shown; a personal signature always takes precedence.</li>
          </ul>
        </div>
      )}
    </Card>
  );
}

export function BrowserCacheGuideEn() {
  return (
    <Card className="help-card" data-section="browser-cache">
      <h2>How to Clear the Browser Cache (Safari, Chrome)</h2>
      <p className="help-intro">
        If an update doesn’t show up in this portal or in Outlook on the web, your browser may still be
        serving old files from its cache. Clearing the cache usually fixes it right away. Clearing{' '}
        <strong>only the cache</strong> doesn’t sign you out; if you also delete cookies and site data,
        you’ll need to sign in to the portal again.
      </p>

      <div className="help-callout help-callout-steps">
        <strong>Safari (Mac) — clear only the cache:</strong>
        <ol>
          <li>
            Go to Safari &gt; <strong>Settings</strong> &gt; <strong>Advanced</strong> and turn on{' '}
            <strong>Show features for web developers</strong>.
          </li>
          <li>
            From the <strong>Develop</strong> menu that appears in the menu bar, choose{' '}
            <strong>Empty Caches</strong> (shortcut: <code>Option + Command + E</code>).
          </li>
          <li>Reload the page.</li>
        </ol>
      </div>

      <div className="help-callout help-callout-steps">
        <strong>Safari (Mac) — clear only this site (signs you out):</strong>
        <ol>
          <li>
            Go to Safari &gt; <strong>Settings</strong> &gt; <strong>Privacy</strong> &gt;{' '}
            <strong>Manage Website Data…</strong>
          </li>
          <li>Type <code>imza.martinci.com</code> in the search box (for Outlook on the web, <code>outlook.office.com</code>).</li>
          <li>Click <strong>Remove</strong>, then <strong>Done</strong>, and reload the page.</li>
        </ol>
      </div>

      <div className="help-callout help-callout-steps">
        <strong>Safari (iPhone / iPad):</strong>
        <ol>
          <li>
            Open <strong>Settings</strong> &gt; <strong>Safari</strong> (on iOS 18 and later,{' '}
            <strong>Settings</strong> &gt; <strong>Apps</strong> &gt; <strong>Safari</strong>).
          </li>
          <li>
            Tap <strong>Clear History and Website Data</strong> and confirm. This signs you out of all
            sites.
          </li>
        </ol>
      </div>

      <div className="help-callout help-callout-steps">
        <strong>Chrome (Windows / Mac):</strong>
        <ol>
          <li>
            Press <code>Ctrl + Shift + Delete</code> (<code>Command + Shift + Delete</code> on Mac) — or use
            the <strong>⋮</strong> menu at the top right &gt; <strong>Delete browsing data</strong>.
          </li>
          <li>Choose <strong>All time</strong> as the time range.</li>
          <li>
            Leave only <strong>Cached images and files</strong> checked (if you don’t check cookies, you
            stay signed in).
          </li>
          <li>Click <strong>Delete data</strong> and reload the page.</li>
        </ol>
      </div>

      <p className="help-body">
        As a quick alternative, reload the page bypassing the cache: in Chrome,{' '}
        <code>Ctrl + Shift + R</code> (<code>Command + Shift + R</code> on Mac); in Safari, hold{' '}
        <code>Shift</code> while clicking the reload button.
      </p>

      <p className="help-note">
        Note: these steps refresh files in the browser. Classic Outlook (the Windows app) stores add-in
        files in its own folder, independent of the browser; for that, follow the steps in the
        “Why Updates Are Delayed” guide above.
      </p>
    </Card>
  );
}
